import camelCase from 'lodash.camelcase';
import moment from 'moment';
import * as React from 'react';
import { Helmet } from 'react-helmet';

import { CoachExpertDtoType } from 'types/services/magnolia/dtos/CoachExpertDto';
import { WithRouterType } from 'types/withRouter';

import { EventCustomFeedbackCodes, EventDto, EventLevelsValues, EventTypesValues } from 'defs/events';
import { EventFeedbackCodes, EventLevels, EventTypes } from 'defs/events';
import { trackProgram, trackProgramReplace, trackProgramStart } from 'defs/ga/track/programs';
import routes, { getBaseTitle, getSeoDataFromViewId } from 'defs/routes';

import { push as dataLayerPush } from 'helper/ga';
import i18n from 'helper/i18n';
import appConfig from 'helper/mapBackendConfig';
import noop from 'helper/noop';
import { compileDynamicRoute, template } from 'helper/string';

import { ProgramSlugsType } from 'store/programPreview';
import { TranslationLabelValue } from 'store/translation';

import { delayed } from 'lib/Job';

import { IconNumberProps } from 'components/atoms/IconNumber';
import { ImageProps } from 'components/atoms/Image/Image';
import Loader from 'components/atoms/Loader';
import { WithVirtualPageView } from 'components/hoc/withVirtualPageView';
import withVirtualPageView from 'components/hoc/withVirtualPageView';
import ProgramContents from 'components/molecules/Program/ProgramContents';
import ProgramExperts from 'components/molecules/Program/ProgramExperts';
import ProgramInfo from 'components/molecules/Program/ProgramInfo';
import ProgramStage from 'components/molecules/Program/ProgramStage';
import Error40x from 'components/views/Error40x';
import { TargetTypes } from 'defs/notifications';

export type ContainsType = {
	days?: number;
	workouts?: number;
	recipes?: number;
	todos?: number;
	tips?: number;
};

export type ProgramPreviewProps = WithVirtualPageView & {
	id: string; // route param
	slug: string; // slug url
	externalId: string; // programId
	currentViewID: string;
	isProgramPreviewLoaded: boolean;
	isProfileLoaded: boolean;
	isPreviewUser: boolean;
	isLoadingProgram: boolean;
	isNewProgram: boolean;
	error?: any;
	tools: Object[];
	title?: string;
	points: string;
	level: string;
	nextLevelThreshold: string;
	seoTitle?: string;
	dsgvo: boolean | null;
	showDsgvoReminder: boolean;
	slugs: ProgramSlugsType;
	isAuthenticated: boolean;
	description?: string;
	info?: string;
	preview?: string;
	intensity: number;
	hasDynamicWorkouts: boolean;
	hasDynamicRecipes: boolean;
	userIntensity: number;
	metaDescription: string | null;
	goal?: string;
	isRecommended?: boolean;
	contains: ContainsType;
	duration: string;
	previewItems: any[];
	previewImage?: ImageProps;
	userWeight?: number;
	inactive?: boolean;
	countActiveProgram: number;
	hasProgramChanged: boolean;
	isAnamnesisDone: boolean;
	hasAuthenticationLoaded: boolean;
	hideExpertLogo: boolean;
	experts: CoachExpertDtoType[];
	doFlushCurrentPreview: Function;
	hasActivePrograms: boolean;
	languageRoutePaths: TranslationLabelValue;
	LANGUAGE_ROUTE_PATHS_LABEL: string;
	doAddEvents: (evs: EventDto[]) => void;
	doResolveTranslation: (label: string) => void;
	doStartProgram(id: string, targetDate?: string): () => void;
	doLoadPreview(id: string): () => void;
	doSaveRedirectRoutePath(url: string): () => void;
	doResetHasProgramChanged(): () => void;
} & WithRouterType;

export type ProgramPreviewError = {
	data: {
		messageCode: string;
	};
};

export type ProgramPreviewState = {
	showPreviewDateOverlay?: boolean;
	previewDateOverlayDone?: boolean;
	previewDate?: string;
};

export class ProgramPreviewView extends React.Component<ProgramPreviewProps, ProgramPreviewState> {
	public static displayName = 'ProgramPreviewView';
	public static defaultProps = {
		loaded: false,
		intensity: 0,
		userIntensity: 0,
		doLoadPreview: noop,
		doAddEvents: noop,
		doFlushCurrentPreview: noop,
		isPreviewUser: false,
		tools: [],
		contains: {},
		previewItems: [],
		hideExpertLogo: false,
		error: null,
		id: '',
		userWeight: 0,
		location: { search: '' },
	};

	public alreadyTrackedGA: boolean = false;
	public previewDateInput: { value: string } | null = null;
	public state: ProgramPreviewState = {};
	public today: string = new Date().toISOString().slice(0, 10);

	public componentDidMount() {
		this.props.doLoadPreview(this.props.id);
		this.props.doResolveTranslation(this.props.LANGUAGE_ROUTE_PATHS_LABEL);
	}

	public componentDidUpdate(prevProps: ProgramPreviewProps) {
		const {
			hasAuthenticationLoaded,
			isAuthenticated,
			isProfileLoaded,
			isLoadingProgram,
			isProgramPreviewLoaded,
			location,
			doSaveRedirectRoutePath,
			countActiveProgram,
			history,
			match,
		} = this.props;

		if (this.props.hasProgramChanged) {
			this.props.doResetHasProgramChanged();
			history.replace(routes.today.indexRoute.href);
		}

		if (countActiveProgram === 0 && isAuthenticated === false && hasAuthenticationLoaded) {
			doSaveRedirectRoutePath(compileDynamicRoute(match.path, match.params));
		}

		if (
			!isLoadingProgram &&
			isAuthenticated &&
			isProgramPreviewLoaded &&
			isProfileLoaded &&
			hasAuthenticationLoaded &&
			location.search.search('startProgram=true') >= 0
		) {
			history.push({
				pathname: location.pathname,
				search: '',
			});

			this.handleShouldStartProgram();
		}
	}

	public componentWillUnmount() {
		delayed(this.props.doFlushCurrentPreview, 50);
	}

	public getContentTypes = (contains: ContainsType): IconNumberProps[] => {
		const { hasDynamicWorkouts, hasDynamicRecipes } = this.props;

		return [
			{
				type: 'workout',
				count: contains.workouts ? contains.workouts : 0,
				label: i18n('react.impulsCoach.components.ProgramStage.contains.workoutsCaption'),
				text: hasDynamicWorkouts ? i18n('react.impulsCoach.components.ProgramStage.personalizedText') : '',
			},
			{
				type: 'recipe',
				count: contains.recipes ? contains.recipes : 0,
				label: i18n('react.impulsCoach.components.ProgramStage.contains.recipeCaption'),
				text: hasDynamicRecipes ? i18n('react.impulsCoach.components.ProgramStage.personalizedText') : '',
			},
			{
				type: 'task',
				count: contains.todos ? contains.todos : 0,
				label: i18n('react.impulsCoach.components.ProgramStage.contains.taskCaption'),
			},
			{
				type: 'tip',
				count: contains.tips ? contains.tips : 0,
				label: i18n('react.impulsCoach.components.ProgramStage.contains.tipCaption'),
			},
		];
	};

	public handleShouldStartProgram = () => {
		const {
			isAuthenticated,
			doAddEvents,
			isAnamnesisDone,
			hasActivePrograms,
			isRecommended,
			isPreviewUser,
			doSaveRedirectRoutePath,
			match,
			dsgvo,
			showDsgvoReminder,
		} = this.props;

		if (isPreviewUser && !this.state.previewDateOverlayDone) {
			this.setState({
				previewDateOverlayDone: false,
				showPreviewDateOverlay: true,
			});
			return true;
		}

		if (isAuthenticated === false) {
			const event: EventDto = {
				eventLevel: EventLevels.NotAvailable as EventLevelsValues,
				code: EventFeedbackCodes.F10,
				eventType: EventTypes.Feedback as EventTypesValues,
			};

			doSaveRedirectRoutePath(compileDynamicRoute(routes.deepLink.startProgram.href, match.params));
			doAddEvents([event]);
			return true;
		}

		if (isAnamnesisDone === false) {
			doAddEvents([
				{
					eventLevel: EventLevels.NotAvailable as 'NA',
					code: EventFeedbackCodes.F11,
					eventType: EventTypes.Feedback as 'FEEDBACK',
				},
			]);
			return true;
		}

		const eventsDsgvo: EventDto[] = [];

		// delete old program and start new one
		const eventOverrideProgram = {
			eventLevel: EventLevels.NotAvailable as 'NA',
			code: EventFeedbackCodes.F04,
			eventType: EventTypes.Feedback as 'FEEDBACK',
			callback: (id: string) => {
				return id === 'replace' ? this.googleAnalyticsTrackProgramReplace() : null;
			},
			payload: {
				programId: this.props.id,
				previewDate: this.state.previewDate,
			},
		};

		// put /override
		const eventProgramDontMatch = {
			eventLevel: EventLevels.NotAvailable as 'NA',
			code: EventFeedbackCodes.F05,
			eventType: EventTypes.Feedback as 'FEEDBACK',
			callback: (id: string) => (id === 'confirm' ? this.googleAnalyticsTrackProgramStart() : null),
			payload: { programId: this.props.id, previewDate: this.state.previewDate },
		};

		if (dsgvo === null) {
			eventsDsgvo.push({
				eventLevel: EventLevels.NotAvailable as 'NA',
				code: EventCustomFeedbackCodes.Dsgvo,
				eventType: EventTypes.Feedback as 'FEEDBACK',
				overwrites: {
					coach: 'LEO',
				},
			});
		}

		if (showDsgvoReminder) {
			eventsDsgvo.push({
				eventLevel: EventLevels.NotAvailable as 'NA',
				code: EventCustomFeedbackCodes.DsgvoReminder,
				eventType: EventTypes.Feedback as 'FEEDBACK',
				overwrites: {
					coach: 'LEO',
				},
			});
		}

		if (hasActivePrograms && isRecommended) {
			doAddEvents([...eventsDsgvo, eventOverrideProgram]);
			return true;
		}

		if (hasActivePrograms && !isRecommended) {
			doAddEvents([
				...eventsDsgvo,
				{
					eventLevel: EventLevels.NotAvailable as 'NA',
					code: EventFeedbackCodes.F04,
					eventType: EventTypes.Feedback as 'FEEDBACK',
					callback: (id: string, onClose: () => void) => {
						if (id === 'confirm') {
							onClose();
							doAddEvents([eventProgramDontMatch]);
						}
					},
					payload: {
						programId: this.props.id,
						previewDate: this.state.previewDate,
					},
					overwrites: {
						targets: {
							primary: {
								callbackId: 'confirm',
								type: TargetTypes.Close,
							},
						},
					},
				},
			]);
			return true;
		}

		if (!hasActivePrograms && !isRecommended) {
			doAddEvents([...eventsDsgvo, eventProgramDontMatch]);
			return true;
		}

		// PULSWM-3995
		// in case of there is no event after dsgvo we need to
		// trigger the startProgram process manually
		if (eventsDsgvo.length > 0) {
			doAddEvents([
				...[
					{
						...eventsDsgvo[0],
						callback: (id: string, onClose: () => void) => {
							if (id !== 'default') {
								return;
							}
							this.props.doStartProgram(this.props.id, this.state.previewDate);
							this.googleAnalyticsTrackProgramStart();
							onClose();
						},
					},
				],
			]);
			return true;
		}

		if (!hasActivePrograms && isRecommended) {
			this.props.doStartProgram(this.props.id, this.state.previewDate);
			this.googleAnalyticsTrackProgramStart();
		}

		return;
	};

	public getGoalLabel(goal?: string): string {
		if (goal) {
			return i18n(`react.impulsCoach.components.ProgramStage.target.${camelCase(goal)}`);
		}

		return '';
	}

	public onPreviewStartClick = () => {
		let targetDate: string | undefined;
		const previewDate: string | undefined = (this.previewDateInput || ({} as any)).value;

		if (previewDate) {
			targetDate = moment(previewDate).format('YYYY[-]MM[-]DD');
		} else {
			targetDate = moment().format('YYYY[-]MM[-]DD');
		}

		this.setState(
			{
				showPreviewDateOverlay: false,
				previewDateOverlayDone: true,
				previewDate: targetDate || undefined,
			},
			() => {
				this.handleShouldStartProgram();
			}
		);
	};

	public renderAuthorInput() {
		return (
			<div>
				<div
					className="a-author-input__overlay--react"
					style={{
						position: 'fixed',
						zIndex: 100,
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						transition: 'all .2s linear',
						backgroundColor: 'rgba(0,0,0,.5)',
					}}
				/>
				<form
					onSubmit={this.onPreviewStartClick}
					className="a-author-input__content--react"
					style={{
						zIndex: 300,
						backgroundColor: 'white',
						transition: 'all .2s linear',
						position: 'fixed',
						top: '50px',
						width: '90%',
						left: '5%',
						right: '5%',
						display: 'block',
						margin: '0 auto',
						padding: '20px',
					}}
				>
					<h4>{i18n('react.impulsCoach.views.programPreview.previewUser.title')}</h4>
					<input
						ref={(input) => (this.previewDateInput = input)}
						type="text"
						placeholder="YYYY-MM-DD"
						style={{ border: '1px solid #ddd', padding: '5px' }}
					/>
					<br />
					<input
						type="submit"
						onClick={(ev) => {
							this.onPreviewStartClick();
							ev.preventDefault();
						}}
						style={{ marginTop: '15px' }}
						value={i18n('react.impulsCoach.views.programPreview.previewUser.startCaption')}
					/>
				</form>
			</div>
		);
	}

	public trackProgramProps() {
		dataLayerPush(
			trackProgram({
				programName: this.props.title,
				action: 'programm_start',
				programStart: this.today,
				programDuration: this.props.duration.toString(),
				language: appConfig.env.language,
				levelStatus: this.props.level,
				levelPoints: this.props.points.toString(),
			})
		);
	}

	public googleAnalyticsTrackProgramStart() {
		dataLayerPush(
			trackProgramStart({
				// programName: this.props.title, not used in target function
				programId: this.props.id,
			})
		);

		this.trackProgramProps();
	}

	public googleAnalyticsTrackProgramReplace() {
		dataLayerPush(trackProgramReplace({ programName: this.props.title, programId: this.props.id }));

		this.trackProgramProps();
	}

	public googleAnalyticsTrackPageView() {
		this.props.trackVirtualPageView({
			coachProgId: `${this.props.id}`,
			coachProgNm: this.props.title,
		});
	}

	get seoData(): ReturnType<typeof getSeoDataFromViewId> {
		const { currentViewID } = this.props;
		return getSeoDataFromViewId(currentViewID);
	}

	public render() {
		const {
			isProgramPreviewLoaded,
			isProfileLoaded,
			isPreviewUser,
			isLoadingProgram,
			tools,
			title,
			seoTitle,
			description,
			info,
			preview,
			intensity,
			userIntensity,
			goal,
			isAuthenticated,
			contains,
			inactive,
			previewImage,
			previewItems,
			userWeight,
			isRecommended,
			isNewProgram,
			error,
			slugs,
			metaDescription,
			languageRoutePaths,
			externalId,
			slug,
			experts,
			hideExpertLogo,
		} = this.props;
		const seoData = this.seoData;

		if (isProgramPreviewLoaded && (isProfileLoaded || !isAuthenticated)) {
			if (!isPreviewUser && !this.alreadyTrackedGA) {
				this.googleAnalyticsTrackPageView();
				this.alreadyTrackedGA = true;
			}

			const compiledSeoTitle = template(seoData.title, {
				programTitle: seoTitle || title,
			});

			const compiledOgTitle = template(seoData.og.title, {
				programTitle: seoTitle || title,
			});

			return (
				<div>
					<Helmet>
						<title>{`${compiledSeoTitle} ${getBaseTitle()}`}</title>
						<link rel="canonical" href={document.location.href.replace(externalId, slug)} />
						<meta property="og:title" content={compiledOgTitle} />
						<meta name="description" content={metaDescription || compiledOgTitle} />
						{slugs &&
							languageRoutePaths &&
							Object.keys(slugs).map((lang: string) => {
								if (!languageRoutePaths[lang]) {
									return null;
								}

								return (
									<link
										key={`href-lang-alt-${lang}`}
										rel="alternate"
										hrefLang={lang}
										href={`${appConfig.env.coachBaseUrl.replace(
											`/${appConfig.env.language}/`,
											`/${lang}/`
										)}${compileDynamicRoute(languageRoutePaths[lang], {
											id: slugs[lang],
										})}`}
									/>
								);
							})}
						{previewImage && (
							<meta
								property="og:image"
								content={
									previewImage.original ||
									((previewImage as any).fallback ? (previewImage as any).fallback.x1 : '' || '') ||
									''
								}
							/>
						)}
					</Helmet>
					<ProgramStage
						isNewProgram={isNewProgram}
						isLoadingProgram={isLoadingProgram}
						isRecommended={isRecommended}
						title={title}
						image={previewImage}
						roof={this.getGoalLabel(goal)}
						intensity={intensity}
						userIntensity={userIntensity}
						inactive={inactive}
						contains={contains}
						onStartProgram={this.handleShouldStartProgram}
						contentTypes={this.getContentTypes(contains)}
					/>
					{isPreviewUser && this.state.showPreviewDateOverlay && this.renderAuthorInput()}
					<ProgramInfo
						userWeight={userWeight}
						isAuthenticated={isAuthenticated}
						contains={contains as any}
						support={{ list: tools }}
						lead={description}
						info={info}
					/>
					{experts.length > 0 && (
						<ProgramExperts
							title={i18n('react.impulsCoach.views.programPreview.experts.title')}
							experts={experts}
							hideExpertLogo={hideExpertLogo}
						/>
					)}
					<ProgramContents
						inactive={inactive}
						previewItems={previewItems}
						onStartProgram={this.handleShouldStartProgram}
						preview={preview}
					/>
				</div>
			);
		}

		if (error instanceof Error) {
			return <Error40x />;
		}

		return <Loader />;
	}
}

export default withVirtualPageView(ProgramPreviewView as any);
